import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  FormControl,
  Grid,
  List,
  ListItemButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useState } from "react";
import {
  DEFAULT_DRAG_HANDLE_SELECTOR,
  NO_DRAG_SELECTOR,
} from "../../../../app/constants";
import { LanguageSkill } from "../../../../features/orders/orderTypes";
import { LanguageSkillCode } from "../../../../features/talents/talentAPI";
import useLanguageSkills from "../../../../hooks/useLanguageSkills";
import theme from "../../../../theme";
import { RoundedButton } from "../../styled/styled";
import LanguageSkillLevels from "../languagesForm/LanguageSkillLevels";

type Props = {
  selectedLanguage?: LanguageSkill;
  disabledLanguageCodes?: string[];
  onSaveChanges: (languageCode: LanguageSkillCode, level: string) => void;
  onClose: () => void;
};

export const LanguageSkillEdit = ({
  selectedLanguage,
  disabledLanguageCodes,
  onSaveChanges,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { sortedLanguageSkills } = useLanguageSkills();

  const [skillLevel, setSkillLevel] = useState<string>(
    selectedLanguage?.skillLevel || "",
  );
  const [languageCode, setLanguageCode] = useState<LanguageSkillCode | "">(
    selectedLanguage?.languageCode || "",
  );

  const saveDisabled = !languageCode || !skillLevel;

  return (
    <Box
      justifyContent="center"
      className={DEFAULT_DRAG_HANDLE_SELECTOR}
      sx={{ cursor: "grab" }}
    >
      <FormControl fullWidth size="small">
        <Select
          value={languageCode}
          disabled={!!selectedLanguage}
          onChange={(e) => setLanguageCode(e.target.value as LanguageSkillCode)}
        >
          {sortedLanguageSkills.map((languageSkill) => (
            <MenuItem
              key={languageSkill.id}
              value={languageSkill.code}
              disabled={disabledLanguageCodes?.includes(languageSkill.code)}
            >
              {languageSkill.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <List>
        {LanguageSkillLevels.map((level) => {
          const selected = skillLevel === level;
          const typography = {
            fontSize: "14px",
            color: selected ? theme.palette.primary.main : "#895FDFE5",
            font: theme.typography.header.fontFamily,
          };

          return (
            <ListItemButton
              className={NO_DRAG_SELECTOR}
              onClick={() => setSkillLevel(level)}
              key={"level-" + level}
              selected={skillLevel === level}
              sx={{
                marginTop: "10px",
                marginBottom: "10px",
                border: selected ? "2px solid" : "1px solid",
                borderRadius: "8px",
                borderColor: selected
                  ? theme.palette.primary.main
                  : "#895FDFE5",
              }}
            >
              <Grid container>
                <Grid item xs={9} container>
                  <Typography
                    sx={{
                      ...typography,
                      fontWeight: 700,
                    }}
                  >
                    {t("languageSkill:" + level)}
                  </Typography>
                </Grid>
                <Grid item container xs={3} justifyContent="right">
                  {skillLevel === level && <CheckIcon color="success" />}
                </Grid>
                <Grid item xs={12} container alignItems="center">
                  <Typography
                    sx={{
                      ...typography,
                      fontWeight: 400,
                    }}
                  >
                    {t("languageSkill:" + level + "_description")}
                  </Typography>
                </Grid>
              </Grid>
            </ListItemButton>
          );
        })}
      </List>
      <Box display="flex" justifyContent="space-between" width="100%">
        <RoundedButton variant="outlined" onClick={onClose}>
          {t("cancel")}
        </RoundedButton>
        <RoundedButton
          disabled={saveDisabled}
          variant="contained"
          onClick={() =>
            languageCode && onSaveChanges(languageCode, skillLevel)
          }
        >
          {t("save")}
        </RoundedButton>
      </Box>
    </Box>
  );
};
